/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions, issueLoyalty, IssueLoyaltyInput } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { IHeaderData } from './header.data';
import { CommerceProperty, Customer, CustomersDataActions, LoyaltyCard, StoreOperationsDataActions } from '@msdyn365-commerce/retail-proxy';
import jwtDecode from 'jwt-decode';
import { subscribeCustomer } from '../../../shared/functions/newsletter';
import {
    HeaderHelper,
    HeaderHelperConfiguration,
    saveCartCookie,
    GENDER_COOKIE_NAME,
    LocaleGenderUrls,
    GenderCookieValue
} from './components/headerHelper.component';
import { reaction } from 'mobx';
import { getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { CART_COOKIE_NAME, getCookieValueFromContext } from '../../../shared/functions/cookie';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<IHeaderData>> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;
    private readonly telemetryContent: ITelemetryContent;

    public constructor(props: IHeaderViewProps & IHeaderProps<IHeaderData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName ?? '',
            this.props.friendlyName,
            this.props.telemetry
        );
        this.menuNode = React.createRef();
    }

    public componentDidMount(): void {
        //BS CUSTOM - log datalayer push events as needed
        const telemetry = this.props.context.telemetry;
        const dataLayer = (window.dataLayer = window.dataLayer || []);
        let consentStatus: { [key: string]: any } = {};

        // Prevent double-patching
        if ((window as any).__dataLayerTelemetryWrapped) return;
        (window as any).__dataLayerTelemetryWrapped = true;

        // Replay existing events
        for (const item of dataLayer) {
            if (item?.event) {
                if (item.event === 'consent_status') {
                    consentStatus = item;
                } else if (item.event === 'purchase') {
                    telemetry.error(`dataLayer event purchase (existing)`, {
                        'Google Analytics': consentStatus['Google Analytics'],
                        'Google Tag Manager': consentStatus['Google Tag Manager'],
                        'Microsoft Clarity': consentStatus['Microsoft Clarity'],
                        'Usercentrics Consent Management Platform': consentStatus['Usercentrics Consent Management Platform']
                    });
                }
            }
        }

        // Patch push to capture future events
        const originalPush = dataLayer.push;
        dataLayer.push = function(...args: any[]) {
            const result = originalPush.apply(dataLayer, args);

            try {
                for (const item of args) {
                    if (item?.event) {
                        if (item.event === 'purchase') {
                            telemetry.error(`dataLayer event purchase`, {
                                'Google Analytics': consentStatus['Google Analytics'],
                                'Google Tag Manager': consentStatus['Google Tag Manager'],
                                'Microsoft Clarity': consentStatus['Microsoft Clarity'],
                                'Usercentrics Consent Management Platform': consentStatus['Usercentrics Consent Management Platform']
                            });
                        }
                    }
                }
            } catch (err) {
                console.error('Telemetry logging failed:', err);
            }

            return result;
        };

        this.subscribeToNewsletter(this.props);

        if (this.props.context.request.user?.isAuthenticated) {
            // Added delay to get he updated result.
            setTimeout(() => {
                this.fetchLoyalty(this.props);
            }, 3000);
        }

        //BS CUSTOM - Save cart to cookie when it is updated during the user session
        var isFirstRender = true;
        reaction(
            () => this.props.data.cart.result?.cart.CartLines,
            cartLines => {
                //Ignore first as cartlines is initialized on every render, but we are only interested in when it is actually updated
                if (!isFirstRender) {
                    saveCartCookie(CART_COOKIE_NAME, cartLines);
                }
                isFirstRender = false;
            }
        );
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer,
            app,
            context
        } = this.props;

        const { request } = context;
        var renderHelper = false;
        const config: HeaderHelperConfiguration = {
            enableRedirectOnCookie: this.props.config.enableRedirectOnCookie,
            cookieExpiration: app.config.cookieExpiration,
            shouldRedirectToLocale: this.props.config.shouldRedirectToLocale,
            shouldLogOutInvalidLocaleUser: this.props.config.shouldLogOutInvalidLocaleUser,
            deleteCookiesOnLocaleSwitch: this.props.config.deleteCookiesOnLocaleSwitch,
            centerHeader: this.props.config.centerHeader,
            telemetry: this.props.telemetry,
            cartUpdateToggle: this.props.config.cartUpdateToggle,
            removeUserCartItems: this.props.config.removeUserCartItems,
            shouldDisplayCartUpdate: this.props.config.displayCartUpdate,
            cartUpdateMessage: this.props.config.cartUpdateMessage,
            defaultButtonMessage: this.props.config.cartUpdateButtonMessage,
            cartLink: this.props.context.app.routes.cart.destinationUrl,
            cartLinkButtonMessage: this.props.config.cartLinkButtonMessage,
            context: context.actionContext,
            appConfig: this.props.app.config
        };
        if (this.props.data.cart.status !== 'LOADING') {
            renderHelper = true;
            if (this.props.data.cart.result?.cart) {
                config.cart = this.props.data.cart.result.cart;
            }
        }

        return (
            <>
                {renderHelper ? <HeaderHelper config={config} request={request} /> : null}
                <Module {...HeaderTag}>
                    <Node {...HeaderContainer}>
                        <Node
                            {...HeaderTopBarContainer}
                            className={`${HeaderTopBarContainer.className || ''} ${config.centerHeader ? 'center' : ''}`}
                        >
                            {config.centerHeader && <div className='mobile-navicon-container'>{this.props.navIcon}</div>}
                            {!config.centerHeader && (
                                <>
                                    {this._renderLogo()}
                                    {this._renderLogo(true)}
                                </>
                            )}
                            <div className='desktop-navicon-container' ref={this.menuNode}>
                                {this._renderReactFragment(this.props.menuBar)}
                            </div>
                            {config.centerHeader && (
                                <>
                                    {this._renderLogo()}
                                    {this._renderLogo(true)}
                                </>
                            )}
                            <div className='icon-wrapper'>
                                {this._renderReactFragment(this.props.search)}
                                {this.props.preferredStore}
                                {this.props.wishListIconMobile}
                                {this._renderAccountBlock(this.props)}
                                <Node {...Divider} />
                                {this.props.cartIcon}
                                {this._renderReactFragment(this.props.siteOptions)}
                                {!config.centerHeader && <div className='mobile-navicon-container'>{this.props.navIcon}</div>}
                            </div>
                        </Node>
                        <Node {...MobileMenuContainer}>
                            <Node {...MobileMenuBodyContainer}>
                                {this.props.MobileMenuHeader}
                                {this._renderReactFragment(this.props.menuBar)}
                                <Node {...MobileMenuLinksContainer}>
                                    {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                    {this.props.siteOptions}
                                    {this.props.wishListIconMobile}
                                </Node>
                            </Node>
                        </Node>
                    </Node>
                </Module>
            </>
        );
    }

    private _renderLogo(isMobile = false): JSX.Element | null {
        const { config, context, typeName } = this.props;

        const genderCookie = getCookieValueFromContext<GenderCookieValue>(GENDER_COOKIE_NAME, this.props.context);

        const localeGenderUrls: LocaleGenderUrls[] | undefined = this.props.app.config.localeGenderUrls;
        const genderUrlsForCurrentLocale = localeGenderUrls?.find(genderUrls => {
            return genderUrls.locale === context.actionContext.requestContext.locale;
        });
        const genderPath = genderCookie ? genderUrlsForCurrentLocale?.[genderCookie] : undefined;

        const baseUrl: string = this.props.app.routes.home.destinationUrl;
        const link = genderPath ? { linkUrl: { destinationUrl: `${baseUrl}${genderPath}`, type: 'internalLink' } } : config.logoLink;

        return (
            <Logo
                {...{
                    link,
                    image: isMobile ? config.mobileLogoImage ?? {} : config.logoImage,
                    className: isMobile ? 'ms-header__mobile-logo' : 'ms-header__logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName,
                    telemetryContent: this.telemetryContent
                }}
            />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signInLink
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>{this._renderAccountDropdownLinks(props)}</Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    private readonly _renderAccountDropdownLinks = (props: IHeaderViewProps): JSX.Element | null => {
        const { isOBORequest, switchCustomerLink, accountLinks, signOutLink } = props;
        if (isOBORequest) {
            return (
                <>
                    {switchCustomerLink}
                    {signOutLink}
                    {accountLinks ? <Node className='ms-profile-button-splitter' /> : false}
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                </>
            );
        } else {
            return (
                <>
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                    {signOutLink}
                </>
            );
        }
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };

    private readonly _issueLoyalty = async (): Promise<LoyaltyCard | null> => {
        if (this.props.context.request.user?.isAuthenticated) {
            const input = new IssueLoyaltyInput(this.props.context.request.apiSettings);
            return new Promise(resolve => {
                issueLoyalty(input, this.props.context.actionContext)
                    .then(result => {
                        //TODO: Should be information telemetry
                        this.props.context.telemetry.warning('Loyalty Card Issued');
                        resolve(result);
                    })
                    .catch((err: Error) => {
                        this.props.context.telemetry.error(err.message);
                        this.props.context.telemetry.debug('Unable to issue loyalty card');
                        resolve(null);
                    });
            });
        } else {
            return null;
        }
    };

    private async fetchLoyalty(props: IHeaderProps<IHeaderData>) {
        const {
            data: { loyaltyCard, customerInformation }
        } = props;

        const hasLoyaltyAccount = loyaltyCard.result && loyaltyCard.result.CardTenderTypeValue !== undefined;
        const customerPrimaryLoyalty = customerInformation.result?.ExtensionProperties?.find(
            (item: CommerceProperty) => item.Key === 'BS_PRIMARYLOYALTYCARDID' && item.Value?.StringValue
        );
        var newCard: LoyaltyCard | null = null;
        if (!hasLoyaltyAccount) {
            newCard = await this._issueLoyalty();
        }

        if (!customerPrimaryLoyalty || customerPrimaryLoyalty?.Value?.StringValue === '') {
            if (customerInformation.result) {
                const customer: Customer = customerInformation.result;
                var primaryCard = customer.ExtensionProperties?.find((item: CommerceProperty) => item.Key === 'BS_PRIMARYLOYALTYCARDID');
                if (primaryCard && primaryCard.Value) {
                    if (newCard) {
                        primaryCard.Value.StringValue = newCard.CardNumber;
                    } else {
                        const card = await StoreOperationsDataActions.getCustomerLoyaltyCardsAsync(
                            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                            null
                        );
                        primaryCard.Value.StringValue = card[0].CardNumber;
                    }
                }
                const icontext = { callerContext: this.props.context.actionContext };
                await CustomersDataActions.updateAsync(icontext, customer);
            }
        }
    }

    private readonly subscribeToNewsletter = (props: IHeaderProps<IHeaderData>) => {
        if (
            props.context.request.user.isAuthenticated &&
            props.data.customerInformation.result &&
            props.data.customerInformation?.result?.ExtensionProperties?.find(item => item.Key === 'BS_ALLOWRECEIVENEWSLETTER')?.Value
                ?.IntegerValue === 0
        ) {
            const token: any = jwtDecode(props.context.request.user.token);
            if (
                props.config.timeoutForNewsletterCheck &&
                props.config.newsletterChoiceTimeClaim &&
                token[props.config.newsletterChoiceClaim.name] &&
                token[props.config.newsletterChoiceClaim.name] === props.config.newsletterChoiceClaim.yesValue &&
                token[props.config.newsletterChoiceTimeClaim]
            ) {
                // avoid sending requests for old users, since there is presumably a reason they have failed
                const timeoutCutoff = new Date(Date.now() - 1000 * props.config.timeoutForNewsletterCheck);
                const subscribedTime = new Date(token[props.config.newsletterChoiceTimeClaim] * 1000);
                if (subscribedTime >= timeoutCutoff) {
                    subscribeCustomer(props.data.customerInformation.result, props.config.newsletterAttributeName, props.context).catch(
                        (error: Error) => {
                            this.props.telemetry.error('Could not subscribe customer to newsletter - ' + error.message);
                            this.subscribeToNewsletter(this.props);
                        }
                    );
                }
            }
        }
    };
}

export default HeaderView;
